import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, ClipboardList, CalendarPlus, ShieldCheck, CheckSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { JobForLine, LinkedToggle, RelatedBilling } from './RelatedBilling';

// iter318 — Office Work section: aggregates canonical records; every action opens the
// existing workflow (Invoices / Estimates / Schedule Add Return Visit / HSP invoice review).
export const TYPE_META = {
  invoice: { label: 'Invoice', icon: FileText, tone: 'text-[#D4AF37] bg-[#D4AF37]/15', action: 'Review' },
  estimate: { label: 'Estimate', icon: ClipboardList, tone: 'text-sky-300 bg-sky-500/15', action: 'Review' },
  return_visit: { label: 'Return Visit', icon: CalendarPlus, tone: 'text-emerald-300 bg-emerald-500/15', action: 'Schedule' },
  hsp: { label: 'HSP / Warranty', icon: ShieldCheck, tone: 'text-violet-300 bg-violet-500/15', action: 'Review' },
  approval: { label: 'Job Approval', icon: CheckSquare, tone: 'text-orange-300 bg-orange-500/15', action: 'Approve' },
};
export const FILTERS = [
  { key: 'all', label: 'All' }, { key: 'invoice', label: 'Invoices' }, { key: 'estimate', label: 'Estimates' },
  { key: 'return_visit', label: 'Return Visits' }, { key: 'hsp', label: 'HSP / Warranty' }, { key: 'approval', label: 'Approvals' }, { key: 'overdue', label: 'Overdue' },
];
const STATUS_TONE = { 'Needs Review': 'bg-[#D4AF37] text-black', 'Needs Scheduling': 'bg-sky-500 text-black', 'Pending Office Review': 'bg-violet-500 text-white', 'Pending Approval': 'bg-orange-500 text-black' };
const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (d) => { if (!d) return ''; const t = new Date(d); return Number.isNaN(t.getTime()) ? String(d).slice(0, 10) : t.toLocaleDateString(); };
export const matches = (r, f) => (f === 'all' ? true : f === 'overdue' ? !!r.overdue : r.type === f);

const QueueRow = ({ r, canViewEmployees }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const m = TYPE_META[r.type];
  const Icon = m.icon;
  return (
    <>
    <tr data-testid={`office-row-${r.type}-${r.id}`} onClick={() => nav(r.href)} className="border-t border-[#1f1f1f] hover:bg-[#1a1a1a] cursor-pointer transition-colors">
      <td className="py-2.5 pl-3"><span className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-[10px] font-bold uppercase ${m.tone}`}><Icon className="w-3.5 h-3.5" />{m.label}</span></td>
      <td className="py-2.5" onClick={(e) => e.stopPropagation()}>
        {r.customer_id ? <Link data-testid={`office-row-customer-${r.id}`} to={`/admin/customers?open=${r.customer_id}`} className="text-white hover:text-[#D4AF37] text-sm">{r.customer_name || 'Customer'}</Link> : <span className="text-white text-sm">{r.customer_name || '—'}</span>}
        <JobForLine r={r} />
        <div className="text-[10px] text-[#737373] font-mono">{r.job_number || r.work_order || ''}{!r.trade_assigned && <span data-testid={`office-row-no-trade-${r.id}`} className="ml-1.5 text-orange-300 font-sans">Trade Not Assigned</span>}</div>
        <LinkedToggle r={r} open={open} onToggle={() => setOpen((o) => !o)} />
      </td>
      <td className="py-2.5 text-xs text-[#D4D4D4] max-w-[280px]">
        <div className="truncate">{r.description || '—'}</div>
        {r.type === 'return_visit' && r.customer_promised && <div data-testid={`office-row-promised-${r.id}`} className="text-[10px] font-bold text-amber-300">CUSTOMER PROMISED — {r.promised_label}</div>}
        {r.type === 'return_visit' && !r.customer_promised && r.suggested_label && <div className="text-[10px] text-[#A3A3A3]">Suggested: {r.suggested_label}</div>}
      </td>
      <td className="py-2.5 text-xs" onClick={(e) => e.stopPropagation()}>
        {r.tech_id && canViewEmployees ? <Link data-testid={`office-row-tech-${r.id}`} to={`/admin/workforce/employees?open=${r.tech_id}`} className="text-[#D4D4D4] hover:text-[#D4AF37]">{r.tech_name}</Link> : <span className="text-[#A3A3A3]">{r.tech_name || '—'}</span>}
      </td>
      <td className="py-2.5 text-xs text-[#A3A3A3]">{when(r.date)}</td>
      <td className="py-2.5"><span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${r.overdue ? 'bg-red-500 text-white' : STATUS_TONE[r.status] || 'bg-[#262626] text-white'}`}>{r.overdue ? 'Promise Overdue' : r.status}</span></td>
      <td className="py-2.5 text-xs text-white text-right pr-3">{money(r.amount)}</td>
      <td className="py-2.5 pr-3 text-right">
        <Link data-testid={`office-row-action-${r.id}`} to={r.href} onClick={(e) => e.stopPropagation()} className="inline-block rounded-md border border-[#D4AF37]/60 px-3 py-1 text-xs text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors">{m.action}</Link>
      </td>
    </tr>
    {open && r.related_billing?.length > 1 && <tr className="border-t-0"><td /><td colSpan={7} className="pb-2.5 pr-3"><RelatedBilling r={r} /></td></tr>}
    </>
  );
};

// iter319 — phone layout: compact tappable rows instead of a wide table.
const MobileRow = ({ r }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const m = TYPE_META[r.type];
  const Icon = m.icon;
  return (
    <div role="link" tabIndex={0} onClick={() => nav(r.href)} data-testid={`office-mrow-${r.type}-${r.id}`} className="flex items-start gap-3 px-3 py-2.5 border-t border-[#1f1f1f] active:bg-[#1a1a1a] cursor-pointer">
      <span className={`mt-0.5 rounded p-1.5 ${m.tone}`}><Icon className="w-3.5 h-3.5" /></span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center justify-between gap-2">
          <span className="text-sm text-white truncate">{r.customer_name || m.label}</span>
          <span className="text-xs text-white shrink-0">{money(r.amount) === '—' ? '' : money(r.amount)}</span>
        </span>
        <JobForLine r={r} />
        <span className="block text-[11px] text-[#A3A3A3] truncate">{m.label}{r.job_number ? ` · ${r.job_number}` : ''}{r.tech_name ? ` · ${r.tech_name}` : ''}</span>
        {r.type === 'return_visit' && r.customer_promised
          ? <span className="block text-[10px] font-bold text-amber-300">CUSTOMER PROMISED — {r.promised_label}</span>
          : <span className="block text-[11px] text-[#737373] truncate">{r.description}</span>}
        <LinkedToggle r={r} open={open} onToggle={() => setOpen((o) => !o)} />
        {open && r.related_billing?.length > 1 && <div className="mt-1.5"><RelatedBilling r={r} /></div>}
      </span>
      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold ${r.overdue ? 'bg-red-500 text-white' : STATUS_TONE[r.status] || 'bg-[#262626] text-white'}`}>{r.overdue ? 'Overdue' : m.action}</span>
    </div>
  );
};

export const OfficeWorkQueue = ({ rows, filter, setFilter }) => {
  const [limit, setLimit] = useState(12);
  const { user, hasPermission } = useAuth();
  const canViewEmployees = ['super_admin', 'admin'].includes(user?.role) || hasPermission('view_employees') || hasPermission('manage_employees');
  const shown = rows.filter((r) => matches(r, filter));
  return (
    <section data-testid="office-work-queue" id="office-work-queue" className="rounded-lg border border-[#262626] bg-[#111] overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#262626]">
        <div>
          <h2 className="text-white font-['Outfit'] text-lg font-bold">Work Queue</h2>
          <p className="text-[11px] text-[#737373]">Everything waiting on the office. Click a row to open it in its normal workflow.</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.filter((f) => f.key === 'all' || f.key === filter || rows.some((r) => matches(r, f.key))).map((f) => (
            <button key={f.key} data-testid={`office-filter-${f.key}`} onClick={() => { setFilter(f.key); setLimit(12); }}
              className={`rounded-md border px-2.5 py-1 text-[11px] transition-colors ${filter === f.key ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-semibold' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}>
              {f.label} ({rows.filter((r) => matches(r, f.key)).length})
            </button>
          ))}
        </div>
      </div>
      <div className="md:hidden" data-testid="office-queue-mobile">{shown.slice(0, limit).map((r) => <MobileRow key={`m-${r.type}-${r.id}`} r={r} />)}</div>
      {shown.length === 0 && <p data-testid="office-queue-empty" className="px-4 py-8 text-center text-sm text-[#737373]">Nothing waiting here.</p>}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead><tr className="text-[10px] uppercase tracking-wider text-[#737373] text-left">
            <th className="py-2 pl-3">Type</th><th>Customer / Job</th><th>Description</th><th>Tech</th><th>Date</th><th>Status</th><th className="text-right pr-3">Amount</th><th className="text-right pr-3">Action</th>
          </tr></thead>
          <tbody>{shown.slice(0, limit).map((r) => <QueueRow key={`${r.type}-${r.id}`} r={r} canViewEmployees={canViewEmployees} />)}</tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t border-[#262626] text-[11px] text-[#737373]">
        <span data-testid="office-queue-count">Showing {Math.min(limit, shown.length)} of {shown.length}</span>
        {shown.length > limit && <button data-testid="office-queue-more" onClick={() => setLimit((l) => l + 25)} className="text-[#D4AF37] hover:underline">Show more</button>}
      </div>
    </section>
  );
};

export default OfficeWorkQueue;
